import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Plus, Trash, Printer, ArrowClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { NpdProjectPicker } from "@/components/NpdProjectPicker";
import { NpdTaskPicker } from "@/components/NpdTaskPicker";
import { projectCustomerName } from "@/lib/projectCustomer";
import { NpdNoteField } from "@/components/NpdNoteField";
import { NpdConsumptionSlip } from "@/components/NpdConsumptionSlip";
import { formatUnit } from "../lib/formatUnit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatQty = (v) => (v == null ? "\u2014" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 }));
const formatDateTime = (iso) => {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "\u2014" : d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const STATUS_TONE = {
  posted: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
  pending: "bg-[#EFF8FF] text-[#0E7C86] border-[#B2DDFF]",
  failed: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]",
  unknown: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
};

// NPD books its own consumption out of the NPD godown (Oct 16 2026). Until now
// this needed an admin on the Cost Center Consumption page; the posting is the
// same SAP Goods & Activity Confirmation, fenced to {site}-NPD and stamped with
// the customer and project so the Activity Report can account for it.
export const NpdConsumptionTab = ({ sites }) => {
  const [siteId, setSiteId] = useState("");
  const [stock, setStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState([]);
  const [newCustomer, setNewCustomer] = useState("");
  const [remark, setRemark] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [taskId, setTaskId] = useState("");
  const [taskName, setTaskName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [posting, setPosting] = useState(false);
  const [log, setLog] = useState([]);
  const [printTarget, setPrintTarget] = useState(null);
  // Oct 19 2026: consumption is normally booked against the issued NPD request
  // it came from, so the issue slip and the consumption can never drift onto a
  // different project or task.
  const [issuedRequests, setIssuedRequests] = useState([]);
  const [requestId, setRequestId] = useState("");

  useEffect(() => { if (!siteId && sites.length) setSiteId(sites[0]); }, [sites, siteId]);

  const loadStock = useCallback(async (site) => {
    if (!site) return;
    setLoadingStock(true);
    try {
      const { data } = await axios.get(`${API}/npd-store-requests/consumption/stock`, { params: { site_id: site } });
      setStock(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the NPD godown");
      setStock(null);
    } finally {
      setLoadingStock(false);
    }
  }, []);

  const loadIssuedRequests = useCallback(async (site) => {
    if (!site) return;
    try {
      const { data } = await axios.get(`${API}/npd-store-requests/issued-requests`, { params: { site_id: site } });
      setIssuedRequests(data.requests || []);
    } catch {
      setIssuedRequests([]);
    }
  }, []);

  const loadLog = useCallback(async (site) => {
    try {
      const { data } = await axios.get(`${API}/npd-store-requests/consumption/log`, { params: { site_id: site || "", limit: 25 } });
      setLog(data.postings || []);
    } catch {
      setLog([]);
    }
  }, []);

  useEffect(() => {
    loadStock(siteId); loadLog(siteId); loadIssuedRequests(siteId);
    setLines([]); setRequestId("");
  }, [siteId, loadStock, loadLog, loadIssuedRequests]);

  // A pending posting is SAP working through the document - poll until settled.
  useEffect(() => {
    if (!log.some((p) => p.status === "pending")) return undefined;
    const t = setInterval(() => loadLog(siteId), 4000);
    return () => clearInterval(t);
  }, [log, siteId, loadLog]);

  useEffect(() => {
    if (!printTarget) return undefined;
    const t = setTimeout(() => window.print(), 60);
    const done = () => setPrintTarget(null);
    window.addEventListener("afterprint", done);
    return () => { clearTimeout(t); window.removeEventListener("afterprint", done); };
  }, [printTarget]);

  const visibleStock = useMemo(() => {
    const rows = stock?.items || [];
    const q = search.trim().toUpperCase();
    return q ? rows.filter((r) => r.product_id.toUpperCase().includes(q) || (r.description || "").toUpperCase().includes(q)) : rows;
  }, [stock, search]);

  const selectedRequest = useMemo(
    () => issuedRequests.find((r) => r.request_id === requestId) || null,
    [issuedRequests, requestId],
  );

  const pickRequest = (id) => {
    setRequestId(id);
    const req = issuedRequests.find((r) => r.request_id === id);
    if (!req) { setLines([]); return; }
    setProjectId(req.project_id || "");
    setProjectName(req.project_name || "");
    setTaskId(req.sap_task_id || "");
    setTaskName(req.sap_task_name || "");
    setNewCustomer(req.customer_name || req.new_customer_name || "");
    setRemark(req.remark || "");
    const inGodown = (pid) => (stock?.items || []).find((i) => i.product_id === pid);
    setLines(req.lines.filter((l) => l.remaining_qty > 0).map((l) => {
      const have = inGodown(l.product_id);
      return {
        product_id: l.product_id, description: l.description,
        available: have ? have.qty : 0, uom: have?.uom || l.uom,
        quantity: String(Math.min(l.remaining_qty, have ? have.qty : l.remaining_qty)),
        remaining_qty: l.remaining_qty,
      };
    }));
  };

  const clearRequest = () => {
    setRequestId("");
    setLines([]); setProjectId(""); setProjectName(""); setTaskId(""); setTaskName("");
    setNewCustomer(""); setRemark("");
  };

  const addLine = (row) => {
    if (lines.some((l) => l.product_id === row.product_id)) return toast.error(`${row.product_id} is already on the list`);
    setLines((prev) => [...prev, { product_id: row.product_id, description: row.description, available: row.qty, uom: row.uom, quantity: "" }]);
  };

  const missingField = useMemo(() => {
    if (!siteId) return "Select a site";
    if (lines.length === 0) return "Add at least one item from the NPD godown";
    if (lines.some((l) => !(Number(l.quantity) > 0))) return "Every line needs a quantity";
    const over = lines.find((l) => Number(l.quantity) > l.available);
    if (over) return `${over.product_id}: only ${formatQty(over.available)} ${formatUnit(over.uom)} in the godown`;
    if (!projectId) return "Pick the SAP project this is for";
    if (!taskId) return "Pick the task this material is for";
    if (!remark.trim()) return "Add a note saying why you need this material";
    const overRequest = lines.find((l) => l.remaining_qty !== undefined && Number(l.quantity) > l.remaining_qty);
    if (overRequest) return `${overRequest.product_id}: only ${formatQty(overRequest.remaining_qty)} left on ${requestId}`;
    return "";
  }, [siteId, lines, projectId, taskId, remark, requestId]);

  const submit = async () => {
    setPosting(true);
    try {
      const { data } = await axios.post(`${API}/npd-store-requests/consumption/submit`, {
        site_id: siteId,
        new_customer_name: newCustomer.trim(),
        remark: remark.trim(),
        project_id: projectId,
        task_id: taskId,
        request_id: requestId || null,
        idempotency_key: idempotencyKey,
        lines: lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) })),
      });
      toast.success(`Sent to SAP - posting ${(data.id || data._id || "").slice(0, 8)} is being confirmed`);
      setLines([]);
      setRemark("");
      setNewCustomer("");
      setProjectId("");
      setTaskId("");
      setTaskName("");
      setRequestId("");
      loadIssuedRequests(siteId);
      setProjectName("");
      setConfirmOpen(false);
      loadLog(siteId);
      setTimeout(() => loadStock(siteId), 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "SAP did not accept this consumption");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="npd-consumption-tab">
      {printTarget && createPortal(<NpdConsumptionSlip posting={printTarget} />, document.body)}

      <div className="bg-white border border-[#D0D5DD] rounded-sm">
        <div className="px-4 py-3 border-b border-[#EAECF0] flex items-center gap-3 flex-wrap">
          <h2 className="text-sm font-bold text-[#101828]">Consume From NPD Godown</h2>
          <Badge variant="outline" className="text-[10px] bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" data-testid="npd-consumption-live-badge">LIVE SAP POSTING</Badge>
          <span className="text-xs text-[#667085]" data-testid="npd-consumption-context">
            {stock ? `${stock.godown} \u2192 ${taskId ? `SAP task ${taskId}` : "a SAP project task"}` : "\u2014"}
            {stock?.as_of ? ` \u00b7 stock as of ${formatDateTime(stock.as_of)}` : ""}
          </span>
          {stock?.unsettled_postings > 0 && (
            <span className="text-[11px] px-2 py-1 rounded-sm bg-[#FFFAEB] text-[#B54708] border border-[#FEDF89]" data-testid="npd-consumption-unsettled-warning">
              An earlier {stock.site_id} posting timed out and SAP may or may not have taken it - an admin has to settle it in the Cost Center Consumption Audit Log before anything else can be posted for this site.
            </span>
          )}
          <Button variant="outline" size="sm" className="h-8 bg-white ml-auto" onClick={() => loadStock(siteId)} data-testid="npd-consumption-refresh-stock">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh Stock
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Oct 19 2026: pick the issued request first - it carries the SAP
              project and task, so the slip and the consumption always match. */}
          <div className="border border-[#B2DDFF] bg-[#F5FBFF] rounded-sm px-3 py-2.5 space-y-2" data-testid="npd-consumption-request-panel">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1 min-w-[260px] flex-1">
                <Label className="text-xs font-bold text-[#344054]">Consume against an issued request</Label>
                <Select value={requestId || "none"} onValueChange={(v) => (v === "none" ? clearRequest() : pickRequest(v))}>
                  <SelectTrigger className="h-9 bg-white" data-testid="npd-consumption-request-select">
                    <SelectValue placeholder={issuedRequests.length ? "Select the request" : "Nothing issued yet"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none" data-testid="npd-consumption-request-none">Without a request (pick project and task myself)</SelectItem>
                    {issuedRequests.map((r) => (
                      <SelectItem key={r.request_id} value={r.request_id} data-testid={`npd-consumption-request-option-${r.request_id}`}>
                        {r.request_id} · {r.project_id} / {r.sap_task_id} · {formatQty(r.remaining_total)} left
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedRequest && (
                <div className="text-[11px] text-[#026AA2]" data-testid="npd-consumption-request-locked">
                  Project, task, customer and note come from <strong>{selectedRequest.request_id}</strong> and cannot be changed here.
                </div>
              )}
            </div>
            {!issuedRequests.length && (
              <p className="text-[11px] text-[#667085]" data-testid="npd-consumption-no-requests">
                No issued NPD request is waiting to be consumed at {siteId || "this site"}. Raise one on the Raise Request tab and the store will issue the material into the NPD godown.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Site *</Label>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger className="h-9 bg-white" data-testid="npd-consumption-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {sites.map((s) => <SelectItem key={s} value={s} data-testid={`npd-consumption-site-option-${s}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Customer Name</Label>
              <Input
                value={newCustomer}
                onChange={(e) => setNewCustomer(e.target.value)}
                disabled={!!selectedRequest}
                placeholder="Filled from the project - edit if needed"
                className="h-9"
                data-testid="npd-consumption-new-customer-input"
              />
              <p className="text-[11px] text-[#667085]" data-testid="npd-consumption-customer-hint">
                {selectedRequest ? "Comes from the request." : "Optional. Filled from the project name where it gives the customer away."}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">SAP Project *</Label>
              {selectedRequest ? (
                <Input value={`${projectId}${projectName ? ` \u00b7 ${projectName}` : ""}`} disabled className="h-9" data-testid="npd-consumption-project-locked" />
              ) : (
                <NpdProjectPicker
                  value={projectId}
                  name={projectName}
                  onPick={(p) => {
                    setProjectId(p.project_id); setProjectName(p.name); setTaskId(""); setTaskName("");
                    setNewCustomer(projectCustomerName(p, newCustomer));
                  }}
                  testIdPrefix="npd-consumption-project"
                />
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">SAP Task *</Label>
              {selectedRequest ? (
                <Input value={`${taskId}${taskName ? ` \u00b7 ${taskName}` : ""}`} disabled className="h-9" data-testid="npd-consumption-task-locked" />
              ) : (
                <NpdTaskPicker
                  projectId={projectId}
                  value={taskId}
                  onPick={(t) => { setTaskId(t.task_id); setTaskName(t.name || ""); }}
                  testIdPrefix="npd-consumption-task"
                />
              )}
              <p className="text-[10px] text-[#667085]" data-testid="npd-consumption-task-hint">
                SAP posts this consumption against the task, not the cost centre.
              </p>
            </div>
            <div className="space-y-1 sm:col-span-2 lg:col-span-1">
              <NpdNoteField
                value={remark}
                onChange={setRemark}
                projectId={projectId}
                lines={lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) || 0 }))}
                label="Note *"
                testIdPrefix="npd-consumption-remark"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-[#D0D5DD] rounded-sm">
              <div className="px-3 py-2 border-b border-[#EAECF0] flex items-center gap-2">
                <MagnifyingGlass size={14} className="text-[#667085]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search the NPD godown..."
                  className="h-7 border-0 shadow-none focus-visible:ring-0 px-0"
                  data-testid="npd-consumption-stock-search"
                />
                <span className="text-[11px] text-[#667085] whitespace-nowrap">{visibleStock.length} item(s)</span>
              </div>
              <div className="max-h-72 overflow-auto">
                <table className="w-full text-[12px]" data-testid="npd-consumption-stock-table">
                  <thead className="bg-[#F9FAFB] sticky top-0">
                    <tr>
                      {["Product", "Description", "In Godown", ""].map((h) => (
                        <th key={h} className="px-2 py-1.5 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingStock && <tr><td colSpan={4} className="px-2 py-6 text-center text-[#98A2B3]">Reading the godown...</td></tr>}
                    {!loadingStock && visibleStock.length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-6 text-center text-[#98A2B3]" data-testid="npd-consumption-stock-empty">
                        {stock?.godown ? `Nothing in ${stock.godown} yet - raise a store request to get material in there first.` : "Select a site"}
                      </td></tr>
                    )}
                    {visibleStock.map((r) => (
                      <tr key={r.product_id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-consumption-stock-row-${r.product_id}`}>
                        <td className="px-2 py-1.5 font-mono font-bold">{r.product_id}</td>
                        <td className="px-2 py-1.5 text-[#475467]">{r.description || "\u2014"}</td>
                        <td className="px-2 py-1.5 tabular-nums">{formatQty(r.qty)} {formatUnit(r.uom)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Button variant="outline" size="sm" className="h-6 bg-white text-[11px]" onClick={() => addLine(r)} data-testid={`npd-consumption-add-${r.product_id}`}>
                            <Plus size={12} className="mr-1" /> Add
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border border-[#D0D5DD] rounded-sm">
              <div className="px-3 py-2 border-b border-[#EAECF0]">
                <h3 className="text-xs font-bold text-[#344054]">To Consume</h3>
              </div>
              <div className="max-h-72 overflow-auto">
                <table className="w-full text-[12px]" data-testid="npd-consumption-lines-table">
                  <thead className="bg-[#F9FAFB] sticky top-0">
                    <tr>
                      {["Product", "Available", "Consume Qty", ""].map((h) => (
                        <th key={h} className="px-2 py-1.5 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-6 text-center text-[#98A2B3]" data-testid="npd-consumption-lines-empty">Add items from the godown on the left.</td></tr>
                    )}
                    {lines.map((l, i) => (
                      <tr key={l.product_id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-consumption-line-row-${l.product_id}`}>
                        <td className="px-2 py-1.5 font-mono font-bold">{l.product_id}</td>
                        <td className="px-2 py-1.5 tabular-nums text-[#475467]">{formatQty(l.available)} {formatUnit(l.uom)}</td>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            min="0"
                            step="any"
                            value={l.quantity}
                            onChange={(e) => setLines((prev) => prev.map((x, xi) => (xi === i ? { ...x, quantity: e.target.value } : x)))}
                            className={`h-7 w-28 ${Number(l.quantity) > l.available ? "border-[#FDA29B]" : ""}`}
                            data-testid={`npd-consumption-qty-input-${l.product_id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Button variant="outline" size="sm" className="h-6 bg-white text-[#B42318]" onClick={() => setLines((prev) => prev.filter((_, xi) => xi !== i))} data-testid={`npd-consumption-remove-${l.product_id}`}>
                            <Trash size={12} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              type="button"
              disabled={!!missingField}
              onClick={() => { setIdempotencyKey(`npd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`); setConfirmOpen(true); }}
              data-testid="npd-consumption-post-button"
            >
              Post Consumption to SAP
            </Button>
            <span className="text-xs text-[#667085]" data-testid="npd-consumption-hint">
              {missingField || `${lines.length} line(s) ready \u00b7 books to SAP task ${taskId}`}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#D0D5DD] rounded-sm">
        <div className="px-4 py-3 border-b border-[#EAECF0] flex items-center gap-3">
          <h2 className="text-sm font-bold text-[#101828]">Recent NPD Consumption</h2>
          <Button variant="outline" size="sm" className="h-8 bg-white ml-auto" onClick={() => loadLog(siteId)} data-testid="npd-consumption-refresh-log">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="npd-consumption-log-table">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {["When", "Site", "Items", "Customer", "Project", "SAP Confirmation", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {log.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#98A2B3]" data-testid="npd-consumption-log-empty">Nothing consumed from the NPD godown yet.</td></tr>
              )}
              {log.map((p, i) => (
                <tr key={p._id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-consumption-log-row-${i}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(p.posted_at || p.created_at)}</td>
                  <td className="px-3 py-2 font-mono">{p.site_id}</td>
                  <td className="px-3 py-2 text-[#475467]">
                    {(p.lines || []).map((l) => `${l.product_id} \u00b7 ${formatQty(l.quantity)} ${formatUnit(l.quantity_uom)}`).join(", ") || "\u2014"}
                  </td>
                  <td className="px-3 py-2">{p.customer_name || "\u2014"}</td>
                  <td className="px-3 py-2 text-[#475467]">{p.project_remark || p.remarks || "\u2014"}</td>
                  <td className="px-3 py-2 font-mono">{p.sap_gac_id || "\u2014"}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[p.status] || ""}`} data-testid={`npd-consumption-status-${i}`}>{p.status}</Badge>
                    {p.error ? <p className="text-[10px] text-[#B42318] mt-1 max-w-xs">{p.error}</p> : null}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="outline" size="sm" className="h-7 bg-white" onClick={() => setPrintTarget(p)} data-testid={`npd-consumption-print-${i}`}>
                      <Printer size={13} className="mr-1" /> Print
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg" data-testid="npd-consumption-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Post this consumption to SAP?</DialogTitle>
            <DialogDescription>
              This is live. The stock leaves <strong>{stock?.godown}</strong> and is charged to SAP project task <strong>{taskId}{taskName ? ` \u00b7 ${taskName}` : ""}</strong> - it cannot be undone from this app.
            </DialogDescription>
          </DialogHeader>
          <div className="text-[12px] space-y-1">
            {lines.map((l) => (
              <div key={l.product_id} className="flex justify-between border-b border-[#F2F4F7] pb-1">
                <span className="font-mono font-bold">{l.product_id}</span>
                <span>{formatQty(Number(l.quantity))} {formatUnit(l.uom)}</span>
              </div>
            ))}
            <p className="text-[#475467] pt-2">
              {newCustomer.trim() ? <>For <strong>{newCustomer.trim()}</strong> &middot; </> : null}{remark}
            </p>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" className="bg-white" onClick={() => setConfirmOpen(false)} data-testid="npd-consumption-cancel-button">Cancel</Button>
            <Button disabled={posting} onClick={submit} data-testid="npd-consumption-confirm-button">
              {posting ? "Posting..." : "Yes, post to SAP"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
